import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DpIconComponent } from '@client-monorepo/common/icon';
import { StoreBranch, StoresApiService } from '@client-monorepo/daily-fintech/stores';
import { TitleSummaryComponent } from '@client-monorepo/common/ui-components';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { ActionHandlerService, ActionType, RedirectionTypeEnum } from '@client-monorepo/common/action-handler';
import { Coordination, LocationService, LocationStatusEnum } from '@client-monorepo/common/location-management';
import { NgxSpinnerModule } from '@digipay/ngx-spinner';
import { NgxBadgeModule } from '@digipay/ngx-badge';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceDetector } from '@client-monorepo/common/utilities';

@Component({
  selector: 'stores-applet-addresses',
  standalone: true,
  imports: [CommonModule, DpIconComponent, TitleSummaryComponent, NgxButtonComponent, NgxSpinnerModule, NgxBadgeModule],
  templateUrl: './addresses.component.html',
  styleUrl: './addresses.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressesComponent implements OnInit {
  storeTrackingCode = input.required<string>();
  addresses = signal<StoreBranch[] | undefined>([]);
  location!: Coordination | undefined;
  showMoreStatus = signal<'visible' | 'hidden' | 'pending'>('hidden');
  actionHandler = inject(ActionHandlerService);
  storeApi = inject(StoresApiService);
  locationService = inject(LocationService);
  size = 10;
  waitingTimerForLocation!: NodeJS.Timeout;
  destroyRef = inject(DestroyRef);
  deviceDetector = inject(DeviceDetector);

  ngOnInit(): void {
    this.initialize();
  }

  initialize(): void {
    this.waitingTimerForLocation = setInterval(() => {
      this.location = this.locationService.defaultLocation;
      this.getStoreBranches();
    }, 2000);
    this.getLocation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.location = res.coordination;
        clearTimeout(this.waitingTimerForLocation);
        this.getStoreBranches();
      });
  }

  getLocation(): Observable<{ coordination: Coordination; state: LocationStatusEnum }> {
    return this.locationService.getGuaranteedLocation();
  }

  getStoreBranches(): void {
    this.storeApi.getStoreBranches('branch-only', this.storeTrackingCode(), this.location as Coordination, this.size).subscribe((res) => {
      this.addresses.set(res.branches);
      if (this.addresses() && this.addresses()!.length < res.totalElements) {
        this.showMoreStatus.set('visible');
      } else {
        this.showMoreStatus.set('hidden');
      }
    });
  }

  goToGoogleMap(address: StoreBranch): void {
    if (this.deviceDetector.isIphone()) {
      this.handleRedirectionInIOS(address);
    } else {
      this.actionHandler.handle({
        type: ActionType.REDIRECT,
        payload: {
          type: RedirectionTypeEnum.blank,
          url: `https://maps.google.com/?q=${address.location.latitude},${address.location.longitude}`,
          params: {
            external: true,
          },
        },
      });
    }
  }

  showMore(): void {
    this.showMoreStatus.set('pending');
    this.size += 10;
    this.getStoreBranches();
  }

  handleRedirectionInIOS(address: StoreBranch): void {
    const query = `${address.location.latitude},${address.location.longitude}`;
    const appUrl = `comgooglemaps://?q=${query}`;
    const fallback = `https://www.google.com/maps/search/?api=1&query=${query}`;

    const start = Date.now();
    setTimeout(() => {
      if (Date.now() - start < 2000) {
        window.open(fallback, '_blank');
      }
    }, 1500);

    window.open(appUrl, '_blank');
  }
}
